import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Bell, AlertCircle, Calendar, Filter, X, RefreshCw } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { FormDatePicker, Card, Spinner, Button, KPICard } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';

const PayoutAlerts = () => {
  const { user: currentUser } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [alerts, setAlerts] = useState([]);
  const [totalPayoutBalance, setTotalPayoutBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchAlerts();
  }, [filters]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.userId) params.userId = filters.userId;
      if (filters.startDate) {
        // Format date as YYYY-MM-DD for backend
        const date = filters.startDate instanceof Date 
          ? filters.startDate 
          : new Date(filters.startDate);
        params.startDate = date.toISOString().split('T')[0];
      }
      if (filters.endDate) {
        // Format date as YYYY-MM-DD for backend
        const date = filters.endDate instanceof Date 
          ? filters.endDate 
          : new Date(filters.endDate);
        params.endDate = date.toISOString().split('T')[0];
      }

      const response = await api.get('/api/payout-alerts', { params });
      console.log('Fetched alerts:', response.data);
      if (response.data && response.data.alerts) {
        setAlerts(response.data.alerts);
        setTotalPayoutBalance(response.data.totalPayoutBalance || 0);
      } else {
        console.warn('Unexpected response format:', response.data);
        setAlerts([]);
        setTotalPayoutBalance(0);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      console.error('Error details:', error.response?.data);
      showError(error.response?.data?.message || 'Failed to fetch alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (alertId) => {
    const confirmed = await confirm({
      title: 'Delete Alert',
      message: 'Are you sure you want to delete this alert?',
      variant: 'warning',
      confirmText: 'Delete',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/payout-alerts/${alertId}`);
      showSuccess('Alert deleted successfully');
      fetchAlerts();
    } catch (error) {
      showError('Failed to delete alert');
    }
  };

  const getAlertType = (message) => {
    if (message?.toLowerCase().includes('overdue')) return 'overdue';
    if (message?.toLowerCase().includes('low balance')) return 'low-balance';
    return 'upcoming';
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'overdue':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700';
      case 'low-balance':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700';
      default:
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'overdue':
        return <AlertCircle size={20} className="text-red-600 dark:text-red-400" />;
      case 'low-balance':
        return <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-400" />;
      default:
        return <Calendar size={20} className="text-blue-600 dark:text-blue-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const overdueCount = alerts.filter(a => getAlertType(a.message) === 'overdue').length;
  const upcomingCount = alerts.filter(a => getAlertType(a.message) === 'upcoming').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <Bell className="text-primary-600 dark:text-primary-400" size={28} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              Payout Alerts
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-1.5 text-sm sm:text-base">
              Monitor payout notifications and alerts
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={fetchAlerts}
          leftIcon={<RefreshCw size={18} />}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
        <KPICard
          title="Overdue"
          value={overdueCount}
          icon={<AlertCircle size={24} />}
          color="secondary"
        />
        <KPICard
          title="Total Payout Balance"
          value={
            <span className="flex items-center gap-1">
              <DirhamSymbol size={20} inline />
              {totalPayoutBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          }
          icon={<Bell size={24} />}
          color="primary"
        />
        <KPICard
          title="Upcoming"
          value={upcomingCount}
          icon={<Calendar size={24} />}
          color="secondary"
        />
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-primary-600 dark:text-primary-400" />
          <h3 className="font-semibold text-primary-900 dark:text-primary-50">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormDatePicker
            label="Start Date"
            name="startDate"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            placeholderText="Select start date"
          />
          <FormDatePicker
            label="End Date"
            name="endDate"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            placeholderText="Select end date"
          />
        </div>
      </Card>

      {/* Alerts List */}
      <div className="space-y-4">
        {alerts.map((alert) => {
          const alertType = getAlertType(alert.message);
          return (
            <Card
              key={alert._id}
              className={`border-l-4 ${getAlertColor(alertType)} p-6`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="mt-1">
                    {getAlertIcon(alertType)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-primary-900 dark:text-primary-50">
                        {alert.user_id 
                          ? `${alert.user_id.firstName || ''} ${alert.user_id.lastName || ''}`.trim() || alert.user_id.email
                          : 'Unknown User'}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs capitalize ${getAlertColor(alertType)}`}>
                        {alertType.replace('-', ' ')}
                      </span>
                    </div>
                    <p className="text-primary-700 dark:text-primary-300 mb-2">{alert.message}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-primary-600 dark:text-primary-400">
                      <span className="flex items-center gap-1">
                        Amount: <DirhamSymbol size={14} inline />{(alert.amount || 0).toLocaleString()}
                      </span>
                      <span>Date: {new Date(alert.date).toISOString().split('T')[0]}</span>
                      <span>Created: {new Date(alert.createdAt).toISOString().split('T')[0]} {new Date(alert.createdAt).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
                {currentUser?.role === 'admin' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(alert._id)}
                    className="text-primary-400 hover:text-error-600 dark:hover:text-error-400"
                    title="Delete"
                  >
                    <X size={20} />
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {alerts.length === 0 && (
        <Card className="text-center py-8">
          <Bell size={48} className="mx-auto text-primary-400 dark:text-primary-500 mb-4" />
          <p className="text-primary-600 dark:text-primary-400">No alerts found</p>
        </Card>
      )}
    </div>
  );
};

export default PayoutAlerts;
